import React, { useEffect, useState } from "react";
import { CameraIcon, TrashIcon } from "@heroicons/react/24/outline";
import { ImageWithFallback } from "../../../../Utils/loader";
import { UrduInput } from "../../../../Components/Shared/UrduInput";
import { ImageSeoFields } from "../../../../Components/Shared/ImageSeoFields";
import { UiModal } from "../../../../Components/Shared/UiModal";

const REQUIRED_WIDTH = 450;
const REQUIRED_HEIGHT = 450;

const EMPTY_FORM = {
  title: "",
  categoryTitle_ur: "",
  description: "",
  description_ur: "",
  custom_url: "",
  image: null,
  image_alt: "",
  image_title: "",
};

const wordCount = (value) =>
  value.trim().split(/\s+/).filter(Boolean).length;

function AddContractCategoryModal({
  modalOpen,
  toggleModal,
  category,
  handleSaveCategory,
}) {
  const [categoryData, setCategoryData] = useState(EMPTY_FORM);
  const [imagePreview, setImagePreview] = useState(null);
  const [titleError, setTitleError] = useState("");
  const [descriptionError, setDescriptionError] = useState("");
  const [customUrlError, setCustomUrlError] = useState("");
  const [imageError, setImageError] = useState("");

  const revokeBlobPreview = (preview) => {
    if (preview && preview.startsWith("blob:")) {
      URL.revokeObjectURL(preview);
    }
  };

  useEffect(() => {
    if (!modalOpen) {
      setImagePreview((prev) => {
        revokeBlobPreview(prev);
        return null;
      });
      setCategoryData(EMPTY_FORM);
      setTitleError("");
      setDescriptionError("");
      setCustomUrlError("");
      setImageError("");
      return;
    }

    if (category) {
      setCategoryData({
        title: category.title || "",
        categoryTitle_ur: category.categoryTitle_ur || "",
        description: category.description || "",
        description_ur: category.description_ur || "",
        custom_url: category.custom_url || "",
        image: category.image || null,
        image_alt: category.image_alt || "",
        image_title: category.image_title || "",
      });
      setImagePreview(category.image || null);
    } else {
      setCategoryData(EMPTY_FORM);
      setImagePreview(null);
    }
    setTitleError("");
    setDescriptionError("");
    setCustomUrlError("");
    setImageError("");
  }, [modalOpen, category]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCategoryData((prevData) => ({
      ...prevData,
      [name]: value,
    }));

    if (name === "title" && titleError) setTitleError("");
    if (name === "custom_url" && customUrlError) setCustomUrlError("");
    if (name === "description") {
      if (wordCount(value) > 100) {
        setDescriptionError("Description must not exceed 100 words.");
      } else if (descriptionError) {
        setDescriptionError("");
      }
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    const objectUrl = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      if (img.width !== REQUIRED_WIDTH || img.height !== REQUIRED_HEIGHT) {
        setImageError(
          `Image must be exactly ${REQUIRED_WIDTH}x${REQUIRED_HEIGHT} pixels.`,
        );
        return;
      }

      setImagePreview((prev) => {
        revokeBlobPreview(prev);
        return URL.createObjectURL(file);
      });
      setCategoryData((prev) => ({ ...prev, image: file }));
      setImageError("");
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      setImageError("Invalid image file.");
    };
    img.src = objectUrl;
  };

  const handleRemoveImage = () => {
    setImagePreview((prev) => {
      revokeBlobPreview(prev);
      return null;
    });
    setCategoryData((prev) => ({ ...prev, image: null }));
    setImageError("");
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    let formValid = true;
    setTitleError("");
    setDescriptionError("");
    setCustomUrlError("");
    setImageError("");

    if (!categoryData.title.trim()) {
      setTitleError("Title is required.");
      formValid = false;
    }

    if (!categoryData.custom_url.trim()) {
      setCustomUrlError("Custom URL is required.");
      formValid = false;
    }

    if (!categoryData.description.trim()) {
      setDescriptionError("Description is required.");
      formValid = false;
    } else if (wordCount(categoryData.description) > 100) {
      setDescriptionError("Description must not exceed 100 words.");
      formValid = false;
    }

    if (!categoryData.image && !imagePreview) {
      setImageError("Image is required.");
      formValid = false;
    }

    if (!formValid) return;

    handleSaveCategory(categoryData, category ? category.id : null);
  };

  const handleCloseModal = () => {
    setCategoryData(EMPTY_FORM);
    setImagePreview((prev) => {
      revokeBlobPreview(prev);
      return null;
    });
    setTitleError("");
    setDescriptionError("");
    setCustomUrlError("");
    setImageError("");
    toggleModal();
  };

  const isEditMode = Boolean(category);

  return (
    <UiModal
      isOpen={modalOpen}
      onClose={handleCloseModal}
      eyebrow="Contracts"
      title={isEditMode ? "Edit Category" : "Add Category"}
      as="form"
      onSubmit={handleSubmit}
      footer={
        <>
          <button
            type="button"
            onClick={handleCloseModal}
            className="ui-btn-cancel min-w-[108px]"
          >
            Cancel
          </button>
          <button type="submit" className="ui-btn-save min-w-[108px]">
            {isEditMode ? "Update" : "Save"}
          </button>
        </>
      }
    >
      <section className="ui-modal-section">
        <h4 className="ui-modal-section-title">Category details</h4>
        <div className="space-y-4">
          <div>
            <label className="ui-form-label">
              Category Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="title"
              value={categoryData.title}
              onChange={handleInputChange}
              placeholder="Enter category title"
              className={`ui-input-field ${
                titleError ? "border-brand-error" : ""
              }`}
            />
            {titleError ? <p className="ui-form-error">{titleError}</p> : null}
            <UrduInput
              label="زمرہ عنوان"
              name="categoryTitle_ur"
              value={categoryData.categoryTitle_ur}
              onChange={handleInputChange}
            />
          </div>

          <div>
            <label className="ui-form-label">
              Custom URL <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="custom_url"
              value={categoryData.custom_url}
              onChange={handleInputChange}
              placeholder="e.g. residential-contracts"
              className={`ui-input-field ${
                customUrlError ? "border-brand-error" : ""
              }`}
            />
            {customUrlError ? (
              <p className="ui-form-error">{customUrlError}</p>
            ) : null}
          </div>

          <div>
            <label className="ui-form-label">
              Description <span className="text-red-500">*</span>
            </label>
            <textarea
              name="description"
              value={categoryData.description}
              onChange={handleInputChange}
              placeholder="Enter category description..."
              rows={4}
              className={`ui-textarea-field ${
                descriptionError ? "border-brand-error" : ""
              }`}
            />
            <div className="mt-1 flex justify-between">
              {descriptionError ? (
                <p className="ui-form-error">{descriptionError}</p>
              ) : (
                <span />
              )}
              <p className="text-xs text-gray-500">
                Words: {wordCount(categoryData.description)}/100
              </p>
            </div>
            <UrduInput
              label="تفصیل"
              name="description_ur"
              value={categoryData.description_ur}
              onChange={handleInputChange}
              type="textarea"
              rows={3}
            />
          </div>
        </div>
      </section>

      <section className="ui-modal-section">
        <h4 className="ui-modal-section-title">Media</h4>
        <div className="space-y-3">
          <label className="ui-form-label">
            Category Image <span className="text-red-500">*</span>
          </label>

          <input
            id="contract-category-image"
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            className="hidden"
          />

          {imagePreview ? (
            <div className="relative">
              <div className="h-48 w-full overflow-hidden rounded-lg border-2 border-gray-200 bg-gray-100">
                <ImageWithFallback
                  src={imagePreview}
                  alt="Category preview"
                  className="h-full w-full object-contain"
                />
              </div>
              <button
                type="button"
                onClick={handleRemoveImage}
                className="absolute right-2 top-2 rounded-full bg-red-500 p-2 text-white transition-colors hover:bg-red-600"
                title="Remove image"
              >
                <TrashIcon className="h-4 w-4" />
              </button>
              <label
                htmlFor="contract-category-image"
                className="mt-2 inline-flex cursor-pointer text-sm font-medium text-[#982598] hover:underline"
              >
                Change image
              </label>
            </div>
          ) : (
            <label
              htmlFor="contract-category-image"
              className="flex h-48 w-full cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 transition-colors hover:border-[#982598]/50 hover:bg-[#982598]/5"
            >
              <span className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-white shadow-sm">
                <CameraIcon className="h-6 w-6 text-[#982598]" />
              </span>
              <span className="font-medium text-gray-700">Upload Image</span>
              <span className="mt-1 text-sm text-gray-400">
                Required size: {REQUIRED_WIDTH} × {REQUIRED_HEIGHT} pixels
              </span>
            </label>
          )}

          {imageError ? <p className="ui-form-error">{imageError}</p> : null}
          <ImageSeoFields
            imageAlt={categoryData.image_alt}
            imageTitle={categoryData.image_title}
            onAltChange={(value) =>
              setCategoryData((prev) => ({ ...prev, image_alt: value }))
            }
            onTitleChange={(value) =>
              setCategoryData((prev) => ({ ...prev, image_title: value }))
            }
          />
        </div>
      </section>
    </UiModal>
  );
}

export default AddContractCategoryModal;
